import { AbTestConfiguration } from '@pubstack/common/src/abTest'
import { Button } from '@pubstack/ui/components/Button'
import { GlobalModalWrapper } from '@pubstack/ui/components/GlobalModalWrapper'
import { Input } from '@pubstack/ui/components/Input'
import { Link } from '@pubstack/ui/components/Link'
import { Actions, Body, Title } from '@pubstack/ui/components/ModalLayout'
import Table, { handleTableSearchAndSort, onColumnSort, TableColumns } from '@pubstack/ui/components/table/Table'
import { TableCell } from '@pubstack/ui/components/table/TableCell'
import { TableRow } from '@pubstack/ui/components/table/TableRow'
import { Toggle } from '@pubstack/ui/components/Toggle'
import { Tooltip } from '@pubstack/ui/components/Tooltip'
import { FunctionComponent, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useAbTests, useDeleteAbTest, useToggleAbTest } from '~/api/app/ab-test.api'
import AbTestVoid from '~/assets/icons/ab_test_void.svg?react'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { useToast } from '~/components/Toast/useToasts'
import { SUPPORT_MAIL } from '~/utils/constants'
import { ActivateAbTestModal } from './ActivateAbTestModal'

const DeleteAbTestModal: FunctionComponent<{ abTest: AbTestConfiguration; onDelete: () => void }> = ({ abTest, onDelete }) => {
  const { close } = useGlobalModal()
  return (
    <GlobalModalWrapper>
      <Title>Remove A/B test configuration</Title>
      <Body className="flex flex-col gap-3">
        <p className="flex flex-col">
          <span>
            You are about to delete A/B test <strong>{abTest.name}</strong>
          </span>
          {abTest.active && <span>This test is currently running.</span>}
        </p>
        <p>Do you confirm your choice ?</p>
      </Body>
      <Actions>
        <Button onClick={close} variant="tertiary" trackingLabel="common-settings-ab-test-deletion-cancel">
          Cancel
        </Button>
        <Button
          onClick={() => {
            onDelete()
            close()
          }}
          variant="negative"
          trackingLabel="common-settings-ab-test-deletion-confirm"
        >
          Remove A/B test
        </Button>
      </Actions>
    </GlobalModalWrapper>
  )
}

const AB_TEST_COLUMNS: TableColumns<AbTestConfiguration> = [
  { name: 'A/B test name', attributeSort: 'name', isSortable: true, order: 'ascending' },
  { name: 'Variants', isSortable: false },
  { name: 'Status', isSortable: false },
  { name: 'Actions', isSortable: false },
]

export const SettingsAbTestPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const modal = useGlobalModal()
  const toast = useToast()
  const user = useAuthenticatedUser()
  const canEnableAbTest = user.getScopeRole() === 'owner' || user.isAdmin
  const { data: abTests = [], isLoading: isLoadingAbTests, isError } = useAbTests()
  const { mutateAsync: toggleAbTest, isPending: isToggling } = useToggleAbTest()
  const { mutateAsync: removeAbTest, isPending: isDeleting } = useDeleteAbTest()
  const [columns, setColumns] = useState<TableColumns<AbTestConfiguration>>(AB_TEST_COLUMNS)
  const { control } = useForm({ defaultValues: { search: '' } })
  const search = useWatch({ control, name: 'search' })

  const isLoading = isLoadingAbTests || isToggling || isDeleting
  const activeCount = abTests.filter((abTest) => abTest.active).length
  const sortedAbTests = handleTableSearchAndSort(columns, abTests, search, ['name'])

  const deleteAbTest = async (abTest: AbTestConfiguration) => {
    try {
      await removeAbTest({ id: abTest.id })
      toast.success(
        <>
          <strong>{abTest.name}</strong> has been deleted.
        </>
      )
    } catch (_e) {
      toast(`Something went wrong when trying to delete ${abTest.name}`)
    }
  }

  const toggleStatus = async (abTest: AbTestConfiguration) => {
    try {
      const updated = await toggleAbTest({ id: abTest.id })
      toast.success(
        <>
          <strong>{updated.name}</strong> has been {updated.active ? 'enabled' : 'disabled'}.
        </>
      )
    } catch (_e) {
      toast.alert(`Something went wrong when trying to change ${abTest.name}'s status`)
    }
  }

  const viewAbTest = (abTest: AbTestConfiguration) => navigate(`view/${abTest.id}`)
  const createAbTest = () => navigate('new')

  const toggle = (abTest: AbTestConfiguration) => (
    <Toggle
      onClick={(event) => {
        event.stopPropagation()
        if (!abTest.active && activeCount > 0) {
          modal.open(ActivateAbTestModal, { abTest, onToggle: () => toggleStatus(abTest) })
        } else {
          toggleStatus(abTest)
        }
      }}
      value={abTest.active}
      disabled={!canEnableAbTest}
      trackingLabel={abTest.active ? 'common-settings-ab-test-activation-unset' : 'common-settings-ab-test-activation-set'}
    />
  )

  if (abTests.length) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between">
          <Input control={control} name="search" label="Search" labelIsPlaceholder iconLeft="search" />
          <Button onClick={createAbTest} trackingLabel="common-settings-ab-test-new">
            Set A/B test
          </Button>
        </div>
        <Table isLoading={isLoading} columns={columns} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
          {sortedAbTests.map((abTest) => (
            <TableRow key={abTest.id} clickable={{ onClick: () => viewAbTest(abTest), trackingLabel: 'common-settings-ab-test-view' }}>
              <TableCell>{abTest.name}</TableCell>
              <TableCell>{abTest.variants.map((value) => value.variant).join(', ')}</TableCell>
              <TableCell>{canEnableAbTest ? toggle(abTest) : <Tooltip title="Only owners can enable a test">{toggle(abTest)}</Tooltip>}</TableCell>
              {/* width 0 shrinks the last cell down to hug the action buttons */}
              <TableCell className="w-0">
                <div className="flex gap-4">
                  <Tooltip title="View">
                    <Button
                      onClick={(event) => {
                        event.stopPropagation()
                        viewAbTest(abTest)
                      }}
                      variant="tertiary"
                      iconName="view"
                      trackingLabel="common-settings-ab-test-icon-view"
                    />
                  </Tooltip>
                  {(!abTest.active || canEnableAbTest) && (
                    <Tooltip title="Remove">
                      <Button
                        onClick={(event) => {
                          event.stopPropagation()
                          modal.open(DeleteAbTestModal, { abTest, onDelete: () => deleteAbTest(abTest) })
                        }}
                        variant="tertiary"
                        iconName="trash"
                        trackingLabel="common-settings-ab-test-delete"
                      />
                    </Tooltip>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </div>
    )
  }

  if (isLoadingAbTests) {
    return <Table isLoading columns={columns} />
  }

  if (isError) {
    return (
      <Table isLoading={isLoading} columns={columns}>
        <TableRow>
          <TableCell colspan={columns.length}>
            <div className="flex flex-col items-center py-8 text-center">
              <h2>An error occurred when trying to get A/B test configurations</h2>
              <p>
                Please refresh the page or contact <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel="common-mail-to-support-navigate" /> if the issue persists.
              </p>
            </div>
          </TableCell>
        </TableRow>
      </Table>
    )
  }

  return (
    <div className="flex flex-auto flex-col items-center justify-center text-center">
      <AbTestVoid className="mb-7 w-[190px]" />
      <div className="mb-9">No A/B test tracked here.</div>
      <Button onClick={createAbTest} trackingLabel="common-settings-ab-test-new">
        Set A/B test
      </Button>
    </div>
  )
}
